import { MapCoordinates } from '../data-access/map-config.tokens';

/** Why a raw coordinate string could not be turned into a valid `MapCoordinates`. */
export type CoordinateParseError = 'empty' | 'unparseable' | 'out_of_range';

/**
 * Result of {@link parseCoordinates}: either the parsed pair or a typed reason the
 * caller maps to a user-facing message. Signed decimal degrees only — DMS and
 * `N`/`S`/`E`/`W`-suffixed formats are out of scope and resolve to `'unparseable'`.
 */
export type ParsedCoordinates =
  | { ok: true; value: MapCoordinates }
  | { ok: false; error: CoordinateParseError };

/**
 * Parse a free-text `latitude, longitude` string (comma- or whitespace-separated)
 * into validated `{ lat, lng }`. Order is latitude first, then longitude. Latitude
 * must be within -90..90 and longitude within -180..180 (same bounds the candidate
 * site schema enforces). Returns a discriminated result rather than throwing.
 */
export function parseCoordinates(raw: string): ParsedCoordinates {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { ok: false, error: 'empty' };
  }

  const tokens = trimmed.split(/[\s,]+/).filter((token) => token.length > 0);
  if (tokens.length !== 2) {
    return { ok: false, error: 'unparseable' };
  }

  const lat = Number(tokens[0]);
  const lng = Number(tokens[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { ok: false, error: 'unparseable' };
  }

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return { ok: false, error: 'out_of_range' };
  }

  return { ok: true, value: { lat, lng } };
}
